import React, { useState, useEffect, useRef } from 'react';
import { X, Smartphone, Globe } from 'lucide-react';
import { gsap } from 'gsap';
import { isAppInstalled, isAndroid, smartLinkHandler } from '../../utils/deepLinking';
import { isMobileDevice } from '../../utils/deviceDetection';
import { useCookieConsent } from '../../contexts/CookieConsentContext';

const STORAGE_KEY = 'baakas_open_in_app_dismissed';
const SESSION_KEY = 'baakas_open_in_app_shown_session';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

// App Store URLs
const IOS_APP_STORE_URL = 'https://apps.apple.com/np/app/baakas-online-shopping-app/id6755933632';
const ANDROID_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.baakas.buyer&hl=en';

/**
 * OpenInAppBanner Component
 * Shows a banner prompting users to open the site in the mobile app
 * Similar to Reddit's "See Reddit in..." banner
 * 
 * Features:
 * - Only shows on mobile devices
 * - Only shows if app is not installed
 * - Dismissible with "Don't show again" option
 * - Remembers user preference (7 days)
 * - Offers "Open in App" and "Continue in Browser" options
 */
const OpenInAppBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const { showBanner: showCookieBanner } = useCookieConsent();
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Only show on mobile devices
    if (!isMobileDevice()) {
      return;
    }

    // Don't show if app is already installed (PWA or native)
    if (isAppInstalled()) {
      return;
    }

    // Check if user has dismissed permanently
    const dismissedData = localStorage.getItem(STORAGE_KEY);
    if (dismissedData) {
      try {
        const { timestamp } = JSON.parse(dismissedData);
        const timeSinceDismissal = Date.now() - timestamp;
        
        // If dismissed within last 7 days, don't show
        if (timeSinceDismissal < DISMISS_DURATION) {
          return;
        }
        // If older than 7 days, clear and show again
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Invalid data, clear it
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    // Check if already shown in this session
    const shownThisSession = sessionStorage.getItem(SESSION_KEY);
    if (shownThisSession === 'true') {
      return;
    }

    // Small delay to ensure page is loaded
    const timer = setTimeout(() => {
      setIsVisible(true);
      sessionStorage.setItem(SESSION_KEY, 'true');
    }, 2000); // Show after 2 seconds

    return () => clearTimeout(timer);
  }, []);

  const handleOpenInApp = () => {
    const currentUrl = window.location.href;
    
    // Use smart link handler to open app
    smartLinkHandler(
      currentUrl,
      undefined,
      () => {
        // App opened successfully
        if (bannerRef.current) {
          gsap.to(bannerRef.current, {
            y: 100,
            opacity: 0,
            duration: 0.3,
            ease: "power2.in",
            onComplete: () => setIsVisible(false)
          });
        } else {
          setIsVisible(false);
        }
      },
      () => {
        // App didn't open, redirect to app store
        if (isAndroid()) {
          window.open(ANDROID_PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
        } else {
          // iOS
          window.open(IOS_APP_STORE_URL, '_blank', 'noopener,noreferrer');
        }
        if (bannerRef.current) {
          gsap.to(bannerRef.current, {
            y: 100,
            opacity: 0,
            duration: 0.3,
            ease: "power2.in",
            onComplete: () => setIsVisible(false)
          });
        } else {
          setIsVisible(false);
        }
      }
    );
  };

  const handleContinueInBrowser = () => {
    if (bannerRef.current) {
      gsap.to(bannerRef.current, {
        y: 100,
        opacity: 0,
        duration: 0.3,
        ease: "power2.in",
        onComplete: () => {
          setIsVisible(false);
          // Save dismissal preference
          if (dontShowAgain) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
              timestamp: Date.now(),
            }));
          } else {
            // Just hide for this session
            sessionStorage.setItem(SESSION_KEY, 'true');
          }
        }
      });
    } else {
      setIsVisible(false);
      // Save dismissal preference
      if (dontShowAgain) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          timestamp: Date.now(),
        }));
      } else {
        sessionStorage.setItem(SESSION_KEY, 'true');
      }
    }
  };

  const handleClose = () => {
    if (bannerRef.current) {
      gsap.to(bannerRef.current, {
        y: 100,
        opacity: 0,
        duration: 0.3,
        ease: "power2.in",
        onComplete: () => {
          setIsVisible(false);
        }
      });
    } else {
      setIsVisible(false);
    }
    // Save dismissal preference
    if (dontShowAgain) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        timestamp: Date.now(),
      }));
    } else {
      sessionStorage.setItem(SESSION_KEY, 'true');
    }
  };

  // Animate in when visible
  useEffect(() => {
    if (isVisible && bannerRef.current) {
      gsap.fromTo(
        bannerRef.current,
        { y: 100, opacity: 0 },
        { 
          y: 0, 
          opacity: 1, 
          duration: 0.5, 
          ease: "back.out(1.7)" 
        }
      );
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div
      ref={bannerRef}
      className="fixed bottom-0 left-0 right-0 z-[9998] px-3 pb-3 md:hidden"
      style={{ 
        paddingBottom: 'max(12px, calc(12px + env(safe-area-inset-bottom)))',
        bottom: showCookieBanner ? '180px' : '64px' // Space above cookie banner if showing, else above bottom nav
      }}
    >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 max-w-sm mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">See Baakas in...</h3>
              <button
                onClick={handleClose}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Options */}
            <div className="p-3 space-y-2">
              {/* Open in App Option */}
              <button
                onClick={handleOpenInApp}
                className="w-full flex items-center justify-between p-2.5 rounded-lg border-2 border-[#da2516] bg-[#da2516]/5 active:bg-[#da2516]/10 transition-colors touch-manipulation"
              >
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-[#da2516] flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">Baakas App</div>
                    <div className="text-xs text-gray-500 truncate">Get the best experience</div>
                  </div>
                </div>
                <div className="px-3 py-1.5 bg-[#da2516] text-white text-xs font-medium rounded-md ml-2 flex-shrink-0">
                  Open
                </div>
              </button>

              {/* Continue in Browser Option */}
              <button
                onClick={handleContinueInBrowser}
                className="w-full flex items-center justify-between p-2.5 rounded-lg border border-gray-200 active:bg-gray-50 transition-colors touch-manipulation"
              >
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">Browser</div>
                    <div className="text-xs text-gray-500 truncate">Continue on this page</div>
                  </div>
                </div>
                <div className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-md ml-2 flex-shrink-0 active:bg-gray-200">
                  Continue
                </div>
              </button>
            </div>

            {/* Don't show again checkbox */}
            <div className="px-3 pb-3 pt-2 border-t border-gray-100">
              <label className="flex items-center gap-2 cursor-pointer touch-manipulation">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  className="w-4 h-4 text-[#da2516] border-gray-300 rounded focus:ring-[#da2516] focus:ring-2 touch-manipulation"
                />
                <span className="text-xs text-gray-500">
                  Don't show this again
                </span>
              </label>
            </div>
          </div>
        </div>
  );
};

export default OpenInAppBanner;

